import { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Alert,
  Button,
  Fieldset,
  Group,
  Input,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import isEqual from 'lodash/isEqual';
import { ruleSchema } from '@billing/validation/rules';
import type { Rule } from '@billing/validation/rules';
import type { MRT_Row, MRT_TableInstance } from 'mantine-react-table';
import { useRuleBuilder } from './shared/useRuleBuilder';
import type { RuleEditorSection } from './shared/constants';

import { RuleEditHeader, RuleEditFooter } from './edit/RuleEditActions';

import { RuleEditLayout, RuleEditSection } from './edit/RuleEditLayout';
import {
  RuleNameInput,
  RuleActionPicker,
  RuleCriteriaFields,
  RuleOutcomeFields,
  RuleActionSummary,
  RuleCriteriaSummary,
  RuleOutcomeSummary,
} from 'pages/rules/components/shared';

interface RuleEditModalProps {
  row: MRT_Row<Rule>;
  table: MRT_TableInstance<Rule>;
  isSaving?: boolean;
  timezone?: string;
  canModify?: boolean;
  onDelete?: () => Promise<void>;
}

interface RuleEditorProps extends RuleEditModalProps {
  onDiscard: () => void;
}

interface RuleEditorSectionConfig {
  key: RuleEditorSection;
  label: string;
  editor: ReactElement;
}

const RuleEditor = ({
  row,
  table,
  isSaving = false,
  timezone = '',
  canModify = true,
  onDiscard,
  onDelete,
}: RuleEditorProps): ReactElement => {
  const rule = row.original;
  const builder = useRuleBuilder({
    initialName: rule.name,
    initialBody: rule.body,
    timezone: timezone || 'UTC',
  });
  const { translate, editSnapshot, isFormSupported } = builder;
  const [savedSnapshot] = useState(editSnapshot);
  const [savedSummaries] = useState(() => ({
    action: <RuleActionSummary builder={builder} />,
    criteria: <RuleCriteriaSummary builder={builder} />,
    outcome: <RuleOutcomeSummary builder={builder} />,
  }));
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasUpdateFailed, setHasUpdateFailed] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const isRuleActive = rule.active || rule.status === 'published';
  const [isActiveOnSave, setIsActiveOnSave] = useState(isRuleActive);
  const updateLock = useRef(false);

  const sections: RuleEditorSectionConfig[] = [
    {
      key: 'name',
      label: translate('pages:rules.builder.edit.name'),
      editor: (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <RuleNameInput builder={builder} />
          <Input.Wrapper label={translate('pages:rules.builder.edit.status')}>
            <SegmentedControl
              role="radiogroup"
              aria-label={translate('pages:rules.builder.edit.status')}
              fullWidth
              radius="xl"
              mt={4}
              value={isActiveOnSave ? 'on' : 'off'}
              disabled={isUpdating || isSaving || !canModify}
              data={[
                {
                  value: 'on',
                  label: translate('pages:rules.builder.edit.on'),
                },
                {
                  value: 'off',
                  label: translate('pages:rules.builder.edit.off'),
                },
              ]}
              onChange={(value) => setIsActiveOnSave(value === 'on')}
            />
          </Input.Wrapper>
        </SimpleGrid>
      ),
    },
    {
      key: 'action',
      label: translate('pages:rules.builder.steps.action'),
      editor: (
        <RuleActionPicker
          value={builder.selectedAction}
          onChange={builder.onActionChange}
          translate={translate}
        />
      ),
    },
    {
      key: 'criteria',
      label: translate('pages:rules.builder.steps.criteria'),
      editor: <RuleCriteriaFields builder={builder} />,
    },
    {
      key: 'outcome',
      label: translate('pages:rules.builder.steps.outcome'),
      editor: <RuleOutcomeFields builder={builder} />,
    },
  ];
  const isSectionChanged = (key: RuleEditorSection): boolean =>
    !isEqual(editSnapshot[key], savedSnapshot[key]) ||
    (key === 'name' && isActiveOnSave !== isRuleActive);
  const changedSectionCount = sections.filter(({ key }) =>
    isSectionChanged(key)
  ).length;
  const editorState = {
    isValid:
      builder.canSubmit &&
      ruleSchema.shape.name.safeParse(builder.ruleName).success,
    isBusy: isUpdating || isSaving,
    isDisabled: isUpdating || isSaving || !canModify,
    hasUnsavedChanges: changedSectionCount > 0,
  };

  const closeModal = (): void => table.setEditingRow(null);

  const executeRuleUpdate = async (
    update: () => Promise<void>
  ): Promise<void> => {
    if (editorState.isDisabled || updateLock.current) return;
    updateLock.current = true;
    setIsUpdating(true);
    setHasUpdateFailed(false);
    try {
      await update();
    } catch {
      setHasUpdateFailed(true);
    } finally {
      setIsUpdating(false);
      updateLock.current = false;
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!editorState.isValid) return;
    await executeRuleUpdate(async () => {
      const values: Record<string, unknown> = {
        name: builder.effectiveRuleName,
        body: builder.body,
        active: isActiveOnSave,
      };
      await table.options.onEditingRowSave({
        row,
        table,
        values,
        exitEditingMode: closeModal,
      });
    });
  };

  const handleDelete = async (): Promise<void> => {
    if (!onDelete || editorState.hasUnsavedChanges) return;
    await executeRuleUpdate(onDelete);
  };

  return (
    <Stack
      gap="md"
      maw="90rem"
      mx="auto"
      style={{ overflowWrap: 'anywhere' }}
      data-testid="edit-rule-modal"
    >
      <RuleEditHeader
        translate={translate}
        disabled={editorState.isDisabled || editorState.hasUnsavedChanges}
        onDelete={
          onDelete ? () => setIsDeleteConfirmationOpen(true) : undefined
        }
      />
      {isDeleteConfirmationOpen && onDelete && (
        <Alert
          color="red"
          title={translate('pages:rules.builder.edit.deleteTitle')}
        >
          <Stack gap="sm">
            <Text size="sm">
              {translate('pages:rules.builder.edit.deleteBody')}
            </Text>
            <Group justify="flex-end">
              <Button
                size="sm"
                variant="subtle"
                disabled={editorState.isBusy}
                onClick={() => setIsDeleteConfirmationOpen(false)}
              >
                {translate('pages:rules.builder.edit.cancel')}
              </Button>
              <Button
                size="sm"
                color="red"
                disabled={
                  editorState.isDisabled || editorState.hasUnsavedChanges
                }
                loading={editorState.isBusy}
                onClick={handleDelete}
              >
                {translate('pages:rules.builder.edit.deleteConfirm')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}
      {hasUpdateFailed && (
        <Alert color="red">
          {translate('pages:rules.builder.edit.failed')}
        </Alert>
      )}
      <RuleEditLayout
        rule={rule}
        timezone={builder.timezone}
        translate={translate}
        preview={savedSummaries}
      >
        {!isFormSupported && (
          <Alert color="blue" mx="lg" mb="md">
            {translate('pages:rules.builder.edit.unsupportedNotice')}
          </Alert>
        )}
        <Fieldset
          disabled={editorState.isDisabled}
          variant="unstyled"
          p={0}
          m={0}
          miw={0}
        >
          {sections.map(({ key, label, editor }) => (
            <RuleEditSection
              key={key}
              title={label}
              changed={isSectionChanged(key)}
              translate={translate}
            >
              <Fieldset
                disabled={
                  editorState.isDisabled || (!isFormSupported && key !== 'name')
                }
                variant="unstyled"
                p={0}
                m={0}
                miw={0}
              >
                {editor}
              </Fieldset>
            </RuleEditSection>
          ))}
        </Fieldset>
      </RuleEditLayout>
      {editorState.hasUnsavedChanges ? (
        <RuleEditFooter
          count={changedSectionCount}
          valid={editorState.isValid}
          saving={editorState.isBusy}
          canModify={canModify}
          translate={translate}
          onDiscard={onDiscard}
          onSave={handleSave}
        />
      ) : (
        <Group justify="flex-end">
          <Button
            variant="subtle"
            disabled={editorState.isBusy}
            onClick={closeModal}
          >
            {translate('pages:rules.builder.edit.back')}
          </Button>
        </Group>
      )}
    </Stack>
  );
};

export const RuleEditModal = (props: RuleEditModalProps): ReactElement => {
  const [revision, setRevision] = useState(0);
  return (
    <RuleEditor
      key={`${props.row.original.id}:${revision}`}
      {...props}
      onDiscard={() => setRevision((current) => current + 1)}
    />
  );
};
